import { useEffect, useState } from "react";

export const STAGES = ["New", "Reached Out", "Contacted", "Interview Scheduled", "Licensing", "Onboarded", "Lost"];

export const STAGE_META = {
  New: { badge: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500", hex: "#0ea5e9", hint: "Fresh submission, not yet contacted" },
  "Reached Out": { badge: "bg-cyan-50 text-cyan-700 border-cyan-200", dot: "bg-cyan-500", hex: "#06b6d4", hint: "Call / text attempted, no reply yet" },
  Contacted: { badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", hex: "#f59e0b", hint: "First touch made" },
  "Interview Scheduled": { badge: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500", hex: "#8b5cf6", hint: "Call or meeting booked" },
  Licensing: { badge: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500", hex: "#6366f1", hint: "Studying / getting licensed" },
  Onboarded: { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", hex: "#10b981", hint: "Contracted and active" },
  Lost: { badge: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400", hex: "#94a3b8", hint: "Not moving forward" },
};

export const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-base sm:text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 transition-colors";

export const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-[#0b1528] px-4 py-2.5 min-h-[44px] sm:min-h-0 text-sm font-semibold text-white shadow-sm hover:bg-[#13213d] active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none";

export const btnSecondary =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 min-h-[44px] sm:min-h-0 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-400 active:scale-[0.98] transition-all disabled:opacity-50";

/** True below Tailwind's `sm` breakpoint (phones). */
export function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const fn = (e) => setMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return mobile;
}

export const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const initials = (name = "") =>
  name.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";

export function relTime(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function answerFor(lead, needle) {
  const a = (lead.answers || []).find((x) => x.question.toLowerCase().includes(needle.toLowerCase()));
  return a ? a.answer : null;
}

export function StageBadge({ stage, className = "", testid }) {
  const m = STAGE_META[stage] || STAGE_META.New;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${m.badge} ${className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {stage}
    </span>
  );
}

export function Avatar({ name, size = "md", className = "" }) {
  const sz = { sm: "h-8 w-8 text-[11px]", md: "h-10 w-10 text-sm", lg: "h-14 w-14 text-lg" }[size];
  return (
    <span className={`flex ${sz} shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0b1528] to-[#1e3a8a] font-bold text-white ${className}`}>
      {initials(name)}
    </span>
  );
}

export function Panel({ title, subtitle, action, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`min-w-0 rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center" data-testid={testid}>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Icon className="h-5 w-5" /></span>
      <p className="mt-3 text-sm font-semibold text-slate-700">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
